import { useEffect, useState } from "react";
import { Bell, AlertTriangle, CheckCircle, DollarSign, CreditCard, TrendingUp, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface Notification {
  id: number;
  type: "success" | "warning" | "alert" | "info";
  message: string;
  icon: JSX.Element;
}

const NotificationSystem = ({ balance, expenses, upcomingBills, show = false }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  
  // Create audio element
  const playNotificationSound = () => {
    const audio = new Audio('/notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(error => console.log('Audio playback failed:', error));
  };

  const generateNotifications = () => {
    let newNotifications: Notification[] = [];

    // Low balance warning
    if (balance < 100) {
      newNotifications.push({
        id: 1,
        type: "warning",
        message: "Your balance is critically low! Consider reducing expenses.",
        icon: <AlertTriangle className="text-red-500 w-5 h-5" />,
      });
    }

    // High spending alert
    if (expenses > balance * 0.8) {
      newNotifications.push({
        id: 2,
        type: "alert",
        message: `You've spent over 80% of your balance this month!`,
        icon: <CreditCard className="text-yellow-500 w-5 h-5" />,
      });
    }

    // Bill reminder
    if (upcomingBills?.length > 0) {
      newNotifications.push({
        id: 3,
        type: "info",
        message: `Upcoming bill: ${upcomingBills[0].name} - $${upcomingBills[0].amount} due on ${upcomingBills[0].dueDate}`,
        icon: <DollarSign className="text-blue-500 w-5 h-5" />,
      });
    }

    return newNotifications;
  };

  useEffect(() => {
    if (show) {
      const newNotifications = generateNotifications();
      setNotifications(newNotifications);
      if (newNotifications.length > 0) {
        playNotificationSound();
      }
    }
  }, [show, balance, expenses, upcomingBills]);

  const dismissNotification = (id: number) => {
    setNotifications(prev => prev.filter(note => note.id !== id));
  };

  return (
    <div className="fixed top-20 right-6 flex flex-col space-y-3 z-50 max-w-sm">
      <AnimatePresence>
        {notifications.map((note) => (
          <motion.div
            key={note.id}
            initial={{ opacity: 0, x: 100, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.8 }}
            transition={{ 
              type: "spring",
              stiffness: 200,
              damping: 20
            }}
            className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-4 
                     border-l-4 border-[#68A511] backdrop-blur-md
                     relative group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#68A511]/10 rounded-lg flex-shrink-0">
                {note.icon}
              </div>
              <div className="flex-1 pr-6">
                <p className="text-gray-700 dark:text-gray-200 text-sm">{note.message}</p>
              </div>
              <button 
                onClick={() => dismissNotification(note.id)}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 
                         transition-opacity duration-200 p-1 hover:bg-gray-100 
                         dark:hover:bg-gray-700 rounded-full"
              >
                <X size={16} className="text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default NotificationSystem; 